import i18n, { intlLocale, rtlNow } from '@/i18n';

// Number formats follow the user's language (cached per locale).
const cache = new Map<string, Intl.NumberFormat>();
const nfFor = (key: string, opts: Intl.NumberFormatOptions) => { const k = intlLocale() + key; let f = cache.get(k); if (!f) { f = new Intl.NumberFormat(intlLocale(), opts); cache.set(k, f); } return f; };
const nf0 = { format: (v: number) => nfFor('0', { maximumFractionDigits: 0 }).format(v) };
const nf2 = { format: (v: number) => nfFor('2', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) };

export const n0 = (v: number | null | undefined) => nf0.format(Number(v ?? 0));
// The signed-in ISP's currency and business time zone (set by the shell from /me; neutral until then).
let CUR = 'USD';
let ZONE = 'UTC';
/** Company currency (from the signed-in tenant). */
export const setCurrency = (c?: string | null) => { if (c) CUR = c; };
/** Business time zone of the signed-in ISP: dates and times are shown in it, whatever the viewer's device says. */
export const setTimezone = (z?: string | null) => { if (z) ZONE = z; };
export const currency = () => CUR;
/** The currency's usual short sign: Rs for PKR, € £ $ … from the browser for the others. */
export function currencySign(c = CUR) {
  if (c === 'PKR') return 'Rs';
  try { return new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: c, currencyDisplay: 'narrowSymbol' }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? c; } catch { return c; }
}
/** Arabic (right-to-left) wraps numbers and money in a first-strong isolate so they never jump around in a sentence. */
const isolate = (s: string) => (rtlNow() ? `\u2068${s}\u2069` : s);
/** Removes direction marks and isolates (for exports and PDFs, whose fonts have no glyph for them). */
export const stripBidi = (s: string) => s.replace(/[\u200e\u200f\u061c\u2066-\u2069\u202a-\u202e]/g, '');
const curFmt = (cur: string, extra: Intl.NumberFormatOptions, key: string) => {
  try { return nfFor(`c${key}${cur}`, { style: 'currency', currency: cur, currencyDisplay: 'symbol', ...extra }); } catch { return null; }
};
/** Whole-unit money in the ISP's currency, written the way the user's language writes it ("PKR 1,234", "1234 €", "1,234 ر.س."). */
export const money = (v: number | null | undefined, cur = CUR) => {
  const x = Math.round(Number(v ?? 0));
  const f = curFmt(cur, { minimumFractionDigits: 0, maximumFractionDigits: 0 }, '0');
  return isolate(f ? f.format(x) : `${cur} ${nf0.format(x)}`);
};
export const money2 = (v: number | null | undefined) => nf2.format(Number(v ?? 0));
/** Money with fractions of a cent (per-message prices: "$0.0135"), up to `d` decimals. */
export const moneyFine = (v: number | string | null | undefined, cur = CUR, d = 4) => {
  const f = curFmt(cur, { minimumFractionDigits: 2, maximumFractionDigits: d }, `f${d}`);
  return isolate(f ? f.format(Number(v ?? 0)) : `${cur} ${Number(v ?? 0).toFixed(d)}`);
};
/** Short number in the user's language: "1.25M" / "1,25 M" / "1.25 مليون". */
export function compact(v: number | null | undefined) {
  const x = Number(v ?? 0); const a = Math.abs(x);
  if (a < 1e3) return nf0.format(x);
  return nfFor(a >= 1e6 ? 'k2' : 'k1', { notation: 'compact', maximumFractionDigits: a >= 1e6 ? 2 : 1 }).format(x);
}
/** Short money for tiles and charts: "Rs 1.25m", "1,25 M €", "1.25 مليون ر.س." — in the ISP's currency and the user's language. */
export const moneyShort = (v: number | null | undefined) => {
  const x = Number(v ?? 0); const big = Math.abs(x) >= 1e6;
  const f = curFmt(CUR, { notation: 'compact', currencyDisplay: 'narrowSymbol', maximumFractionDigits: big ? 2 : 1 } as Intl.NumberFormatOptions, big ? 's2' : 's1');
  return isolate(f ? f.format(x) : `${currencySign()} ${compact(x)}`);
};
/** Percent with the language's sign and spacing: "12.5%", "12,5 %", "12.5٪". The value is already a percentage (12.5 → 12.5 %). */
export const pct = (v: number | null | undefined, d = 1) => nfFor(`p${d}`, { style: 'percent', minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(v ?? 0) / 100);
/** Percent change with its sign: "+2.4%", "−1,5 %", "+2.4٪". */
export const pctSigned = (v: number | null | undefined, d = 1) => nfFor(`ps${d}`, { style: 'percent', signDisplay: 'exceptZero', minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(v ?? 0) / 100);
/** Plain decimal number with exactly d decimals in the user's language ("1.5" / "1,5"). */
export const num = (v: number | string | null | undefined, d = 0) => nfFor(`f${d}`, { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(v ?? 0));
/** Like toFixed(d) but localised, '—' when there is no value: fixed(-21.345, 2) → "-21.35" / "-21,35". */
export const fixed = (v: number | null | undefined, d = 1) => (v == null || !Number.isFinite(Number(v)) ? '—' : num(v, d));
/** Up to d decimals, trailing zeros dropped: numMax(2.5, 2) → "2.5"; '—' when there is no value. */
export const numMax = (v: number | null | undefined, d = 1) => (v == null || !Number.isFinite(Number(v)) ? '—' : nfFor(`m${d}`, { maximumFractionDigits: d }).format(Number(v)));
/** A length in the user's language, to the decimetre: "86.4 m", "1.234 km" (plain text — also for map labels). */
export const metres = (v: number | null | undefined) => (v == null || !Number.isFinite(Number(v)) ? '—'
  : Math.abs(Number(v)) >= 1000 ? `${nfFor('km', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(Number(v) / 1000)} km` : `${nfFor('m1', { maximumFractionDigits: 1 }).format(Number(v))} m`);
/** An area in the user's language: m² up to a hectare, then ha, then km². */
export const areaM2 = (v: number | null | undefined) => (v == null || !Number.isFinite(Number(v)) ? '—'
  : Number(v) >= 1_000_000 ? `${nfFor('km2', { maximumFractionDigits: 3 }).format(Number(v) / 1_000_000)} km²`
  : Number(v) >= 10_000 ? `${nfFor('ha', { maximumFractionDigits: 2 }).format(Number(v) / 10_000)} ha` : `${nfFor('m1', { maximumFractionDigits: 1 }).format(Number(v))} m²`);
export function bytes(v: number | null | undefined, d = 1) {
  let x = Number(v ?? 0); const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']; let i = 0;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${num(x, i ? d : 0)} ${u[i]}`;
}
/** Bit rate: bitrate(1.25e9) → "1.3 Gbps"; '—' when there is no value. */
export function bitrate(v: number | null | undefined, d = 1) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  let x = Number(v); const u = ['bps', 'kbps', 'Mbps', 'Gbps', 'Tbps']; let i = 0;
  while (x >= 1000 && i < u.length - 1) { x /= 1000; i++; }
  return `${num(x, i ? d : 0)} ${u[i]}`;
}
const unit = (n: number, u: 'day' | 'hour' | 'minute' | 'second') => nfFor(`u${u}`, { style: 'unit', unit: u, unitDisplay: 'narrow' }).format(n);
/** "3d 4h" / "3gg 4h" / "3 ي 4 س" — the language's own short time units. */
export function duration(sec: number | null | undefined) {
  const s = Math.max(0, Math.round(Number(sec ?? 0)));
  const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600); const m = Math.floor((s % 3600) / 60);
  if (d) return `${unit(d, 'day')} ${unit(h, 'hour')}`; if (h) return `${unit(h, 'hour')} ${unit(m, 'minute')}`; return `${unit(m, 'minute')} ${unit(s % 60, 'second')}`;
}
export const timeZone = () => ZONE;
export function dt(v: string | Date | null | undefined, withTime = true) {
  if (!v) return '—';
  const d = new Date(v);
  // Always the ISP's local time, whatever the viewer's device time zone is.
  return d.toLocaleString(intlLocale(), { timeZone: ZONE, day: '2-digit', month: 'short', year: 'numeric', ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}) });
}
export const day = (v: string | Date | null | undefined) => dt(v, false);
const rtf = () => new Intl.RelativeTimeFormat(intlLocale(), { numeric: 'auto', style: 'short' });
/** "5 min ago" / "hace 5 min" / "5 min fa" — in the user's language. */
export function ago(v: string | Date | null | undefined) {
  if (!v) return '—';
  const s = (Date.now() - new Date(v).getTime()) / 1000;
  if (s < 60) return rtf().format(0, 'second');
  if (s < 3600) return rtf().format(-Math.floor(s / 60), 'minute');
  if (s < 86400) return rtf().format(-Math.floor(s / 3600), 'hour');
  if (s < 86400 * 30) return rtf().format(-Math.floor(s / 86400), 'day');
  return day(v);
}
/** "in 3 days" / "dentro de 3 días" / "tra 3 giorni"; past dates read as "3 days ago". */
export function until(v: string | Date | null | undefined) {
  if (!v) return '—';
  const s = (new Date(v).getTime() - Date.now()) / 1000;
  if (s < 0) return ago(v);
  if (s < 86400) return rtf().format(Math.floor(s / 3600), 'hour');
  return rtf().format(Math.floor(s / 86400), 'day');
}
export const speed = (kbps: number) => (kbps >= 1024 ? `${nf0.format(Math.round(kbps / 1024))} Mbps` : `${nf0.format(kbps)} kbps`);
/** Percent change between two numbers (null-safe). */
export const change = (curr: number, prev: number) => (prev ? ((curr - prev) / Math.abs(prev)) * 100 : 0);
/**
 * Status/code words for display: the translated status word (st.*) when there is one, otherwise the code made readable
 * ("pending_review" → "Pending Review"). New code should translate labels with t() instead of relying on this.
 */
export const titleCase = (s: string | null | undefined) => {
  const raw = s ?? '';
  const key = raw.toLowerCase();
  if (key && /^[a-z0-9_-]+$/.test(key) && i18n.exists(`st.${key}`)) return i18n.t(`st.${key}`);
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};
